import { Component } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router'; 
import { CommonModule } from '@angular/common';
import { error } from 'console';
import { AuthService } from '../auth.service';
import { LanguageService } from '../language.service';
import { TranslationService } from '../translation.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-episode',
  standalone: true,
  imports: [HeaderComponent, CommonModule, FormsModule],
  templateUrl: './episode.component.html',
  styleUrl: './episode.component.css'
})
export class EpisodeComponent {
  stories: any[] = [];
  isLoading = true;
  errorMessage = '';
  selectedLanguage: string = 'en';
  messageMap: { [key: number]: string } = {};
  storyImgPath = 'https://stories.funtelstudio.com/storyapi/';
  storyId: number | null = null;
  deleteMessage = '';
  isOwnProfile: boolean = false;
  viewingUserId: string = '';
  userId: string = '';
  
  // Monetization properties - CHANGED LOGIC
  isMonetized: boolean = false; // ✅ Admin table se check karenge
  monetizationLoading: boolean = false;
  editingMonetizationId: number | null = null;
  tempMonetizationAmount: number = 5;
  platformFeePercentage: number = 20;
  defaultEpisodePrice: number = 5;

  constructor(
    private http: HttpClient, 
    public translationService: TranslationService, 
    private languageService: LanguageService,
    private router: Router, 
    private route: ActivatedRoute, 
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.selectedLanguage = this.languageService.getCurrentLanguage();
    this.languageService.currentLanguage$.subscribe(lang => {
      this.selectedLanguage = lang;
    });
    
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      this.router.navigate(['login']);
      return;
    }
    
    const param = this.route.snapshot.paramMap.get('idAndTitle');
    if (param) {
      const id = param.split('-')[0];
      this.storyId = Number(id);
      console.log('storyId', this.storyId);
    }
    
    this.loadPlatformFee().then(() => {
      this.checkMonetizationStatus().then(() => { // ✅ CHANGED: checkMonetizationStatus
        this.fetchStories();
      });
    });
  }

  loadPlatformFee(): Promise<void> {
    return new Promise((resolve) => {
      this.http.get<any>('https://stories.funtelstudio.com/storyapi/get_platform_fee.php').subscribe({
        next: (response) => {
          this.platformFeePercentage = parseFloat(response.platform_fee_percentage) || 20;
          this.defaultEpisodePrice = parseFloat(response.default_episode_price) || 5;
          resolve();
        },
        error: () => resolve()
      });
    });
  }

  t(key: string): string {
    return this.translationService.translate(key);
  }

  // ✅ CHANGED: Admin table se monetization status check karo
  checkMonetizationStatus(): Promise<void> {
    return new Promise((resolve) => {
      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        resolve();
        return;
      }

      // Direct admin table se check karo
      this.http.get<any>(`https://stories.funtelstudio.com/storyapi/check_monetization_eligibility.php?user_id=${userId}`, {
        withCredentials: true
      }).subscribe({
        next: (response) => {
          this.isMonetized = response.is_monetized || false;
          console.log('Monetization Status:', this.isMonetized);
          resolve();
        },
        error: () => {
          console.error('Error checking monetization status');
          this.isMonetized = false;
          resolve();
        }
      });
    });
  }

  fetchStories() {
    const userId = this.authService.getCurrentUserId();
    if (!userId) return;

    this.userId = userId.toString();

    this.http.post<{ status: string, data: any[] }>(
      `https://stories.funtelstudio.com/storyapi/draft_get_ep.php?stories_id=${this.storyId}`,
      { user_id: userId },
      { withCredentials: true }
    ).subscribe({
      next: (response) => {
        if (response.status === 'success' && response.data) {
          this.stories = response.data.map(episode => ({
            ...episode,
            monetization_enabled: episode.monetization_enabled || 0,
            monetization_amount: episode.monetization_amount || this.defaultEpisodePrice,
            content_id: episode.id
          }));

          const storyUserId = response.data[0]?.user_id?.toString();
          this.viewingUserId = storyUserId || '';
          this.isOwnProfile = (this.viewingUserId === this.userId);
        }
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load episodes';
        this.isLoading = false;
      }
    });
  }

  // Toggle monetization for an episode (content)
  toggleMonetization(episode: any) {
    // ✅ CHANGED: Admin table ke is_monetized se check karo
    if (!this.isMonetized) {
      alert('Your channel is not monetized yet. Complete eligibility criteria first.');
      return;
    }

    this.monetizationLoading = true;
    const newStatus = episode.monetization_enabled ? 0 : 1;
    
    this.http.post<any>('https://stories.funtelstudio.com/storyapi/update_episode_monetization.php', {
      content_id: episode.content_id,
      monetization_enabled: newStatus,
      monetization_amount: episode.monetization_amount
    }, { withCredentials: true }).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          episode.monetization_enabled = newStatus;
        }
        this.monetizationLoading = false;
      },
      error: () => {
        alert('Error updating monetization');
        this.monetizationLoading = false;
      }
    });
  }

  // Start editing monetization amount for episode
  editMonetizationAmount(episode: any) {
    this.editingMonetizationId = episode.content_id;
    this.tempMonetizationAmount = episode.monetization_amount;
  }

  // Save monetization amount for episode
  saveMonetizationAmount(episode: any) {
    if (this.tempMonetizationAmount < 1 || this.tempMonetizationAmount > 1000) {
      alert('Amount must be between ₹1 and ₹1000');
      return;
    }

    this.monetizationLoading = true;
    this.http.post<any>('https://stories.funtelstudio.com/storyapi/update_episode_monetization.php', {
      content_id: episode.content_id,
      monetization_enabled: episode.monetization_enabled,
      monetization_amount: this.tempMonetizationAmount
    }, { withCredentials: true }).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          episode.monetization_amount = this.tempMonetizationAmount;
          this.editingMonetizationId = null;
        }
        this.monetizationLoading = false;
      },
      error: () => {
        alert('Error updating amount');
        this.monetizationLoading = false;
      }
    });
  }

  // Cancel editing
  cancelEditMonetization() {
    this.editingMonetizationId = null;
  }

  // Rest of your existing methods remain the same...
  goToEdit(story: any) {
    const episodeId = story.episode_id || this.storyId;
    if (!episodeId) return;

    const slug = this.slugify(story.story_title);
    const partId = story.id;
    
    this.router.navigate(
      ['/story-write', `${partId}-${slug}`],
      { queryParams: { episodeId: episodeId } }
    );
  }

  isEpisodeAuthor(episode: any): boolean {
    const currentUserId = this.authService.getCurrentUserId();
    return currentUserId && episode.user_id && episode.user_id.toString() === currentUserId.toString();
  }

  // Get episode price - free for author, actual price for others
  getEpisodePrice(episode: any): number {
    if (this.isEpisodeAuthor(episode)) {
      return 0; // Free for author
    }
    return episode.monetization_amount || 5.00;
  }

  // Check if episode is free for current user
  isEpisodeFreeForUser(episode: any): boolean {
    return this.isEpisodeAuthor(episode);
  }

  goToCreate() {
    if (this.storyId) {
      this.router.navigate(['/creat', this.storyId]);
    }
  }

  slugify(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .replace(/[\s\W-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return '';

    const parsedDate = new Date(dateString.replace(' ', 'T'));
    if (isNaN(parsedDate.getTime())) return 'Invalid date';

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - parsedDate.getTime()) / 1000);

    if (diffInSeconds < 0) return 'Just now';

    const intervals = [
      { label: 'year', seconds: 31536000 },
      { label: 'month', seconds: 2592000 },
      { label: 'week', seconds: 604800 },
      { label: 'day', seconds: 86400 },
      { label: 'hour', seconds: 3600 },
      { label: 'minute', seconds: 60 },
      { label: 'second', seconds: 1 }
    ];

    for (const interval of intervals) {
      const count = Math.floor(diffInSeconds / interval.seconds);
      if (count >= 1) {
        return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
      }
    }

    return 'Just now';
  }

  getValidDate(story: any): string {
    return story.updated_at || story.updated || story.updated_at || story.author_created_at || '';
  }

  deleteStory(contentId: number) {
    if (!confirm('Are you sure you want to delete this episode?')) {
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    this.http.post<{ status: string, message: string }>(
      'https://stories.funtelstudio.com/storyapi/allstory.php?action=deleteContent',
      { id: contentId },
      { headers, withCredentials: true }
    ).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          this.stories = this.stories.filter(s => s.id !== contentId);
          console.log('Deleted:', response.message);
        } else {
          console.warn('Delete failed:', response.message);
        }
      },
      error: (err) => {
        console.error('Error deleting episode:', err);
      }
    });
  }

  editStory(storyData: any) {
    const episodeId = storyData.episode_id;
    const editStoryId = storyData.story_id;
    const title = storyData.story_title;
    
    this.router.navigate(
      ['/story-write', `${editStoryId}-${this.slugify(title)}`],
      { queryParams: { episodeId: episodeId } }
    );
  }
}